import React, { useEffect } from 'react';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import sizes from 'theme/sizes';
import indexPriority from 'theme/z-index';
import { ShoppingCart, X } from 'react-feather';
import Text from 'components/_UI/Text';
import { setRealVh } from 'helpers/generic-css';

export default function CheckoutCollapse({
    children,
    onClose,
    visible = false,
}) {
    useEffect(() => {
        if (visible) {
            document.body.classList.add('no-scroll')
        } else {
            document.body.classList.remove('no-scroll')
        }
    }, [visible])

    useEffect(() => {
        setRealVh();
        window.addEventListener('resize', setRealVh);
        return () => window.removeEventListener('resize', setRealVh);
    })

    return (
        <Container visible={visible} onClick={() => onClose()}>
            <CollapseContainer
                visible={visible}
                onClick={(e) => e.stopPropagation()}
            >
                <CollapseHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <ShoppingCart size={16} color={colors.occasionalPurple} />
                        </IconBadge>
                        <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                            Checkout
                        </Text>
                    </HeaderLeft>
                    {visible && (
                        <CloseButton onClick={() => onClose()}>
                            <X size={20} color={colors.night} />
                        </CloseButton>
                    )}
                </CollapseHeader>
                {children}
            </CollapseContainer>
        </Container>
    )
};

const Container = styled.div`
    height: calc(var(--vh, 1vh) * 100);
    width: 0;
    display: flex;
    align-items: flex-end;
    position: fixed;
    left: 0;
    top: 0;
    z-index: -1000;
    transition: all .35s ease-in-out;

    ${({ visible }) => visible && `
        width: 100%;
        z-index: ${indexPriority.collapse};

        &:after {
            content: '';
            width: 100%;
            height: 100%;
            position: fixed;
            left: 0;
            top: 0;
            background: ${colors.nightDark};
            opacity: .55;
            z-index: ${indexPriority.collapseBackdrop};
        }
    `}
`;

const CollapseContainer = styled.div`
    position: relative;
    background: ${colors.neutralLight};
    box-shadow: ${sizes.none} ${sizes.none} ${sizes.tiny} ${colors.nightLight};
    z-index: ${indexPriority.high};
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 10px 20px 10px;
    height: 100%;
    width: 0;
    opacity: 0;
    visibility: hidden;
    transition: all .35s ease-in-out;

    /**
        Prevents iOS rubber-band scroll.
    */
    max-height: calc(var(--vh, 1vh) * 100);
    -webkit-overflow-scrolling: touch;

    ${({ visible }) => visible && `
        width: 100%;
        opacity: 1;
        visibility: visible;

        @media screen and (min-width: ${breakpoints.md}) {
            padding: 0 20px 50px 20px;
            width: 75%;
            border-radius:  ${sizes.none} ${sizes.small} ${sizes.small} ${sizes.none} ;
        }
    `};

    ${({ widthOnDesktop }) => widthOnDesktop && `
        @media screen and (min-width: ${breakpoints.lg}) {
            width: ${widthOnDesktop} !important;
        }
    `};
`

const CollapseHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    padding: 14px 4px;
    background: ${colors.neutralLight};
    z-index: ${indexPriority.high};
    border-bottom: 1px solid ${colors.night}11;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const IconBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const CloseButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.neutralDark};
    }
`;
